import type {
    IEditorModel,
    IContentMetadata,
    IUrlGenerator,
    UrlGenerator
} from '@ai-content-hub/h5p-server';

import { mergeH5PIntegration } from './h5p-utils';
import { addScripts } from './dom-utils';

declare global {
    /**
     * The H5P core "class" for the editor.
     */
    // eslint-disable-next-line no-var
    var H5PEditor;
    /**
     * Used by the H5P core for namespacing.
     */
    // eslint-disable-next-line no-var
    var ns;
}


/**
 * EAA-1928: rebase `__H5P_BASE__` inside H5P's editor library cache.
 *
 * `h5peditor.js:111` is `ns.libraryRequested = function (libraryName, callback)` and
 * reads `ns.libraryCache[libraryName]` on its first line — the DATA is in the cache,
 * not in the argument. The static bundle stores every absolute URL as the portable
 * `__H5P_BASE__` token, and nothing else resolves it for payloads H5P fetches itself,
 * so without this every editor asset is requested as `/<route>/__H5P_BASE__/...`.
 */
function installLibraryCacheRebase(ns: unknown, staticBase: string | undefined): boolean {
    if (!staticBase || !ns) {
        return false;
    }
    const editorNs = ns as {
        libraryRequested?: (libraryName: string, callback?: unknown) => unknown;
        libraryCache?: Record<string, unknown>;
        __achRebaseInstalled?: boolean;
    };
    // EAA-1928: the guard must test THIS object's own accessor, not a boolean flag.
    //
    // Measured on dev (build 519): the iframe's H5PEditor is a property-COPY of the
    // parent's. The copy reads our accessor through its getter and stores a plain data
    // property, losing the accessor — and it copies `__achRebaseInstalled: true` too.
    // A flag check therefore saw "already installed" on an object we had never touched
    // and skipped the install, after which the iframe's own h5peditor.js assigned an
    // unwrapped function. Descriptor evidence:
    //   {writable: true, enumerable: true, configurable: true, value: f}  <- no setter.
    const existing = Object.getOwnPropertyDescriptor(editorNs, 'libraryRequested');
    if (existing?.set && (existing.set as { __achOwn?: boolean }).__achOwn) {
        return true;
    }
    const REBASED = '__achRebased';

    const wrap = (
        fn: ((libraryName: string, callback?: unknown) => unknown) | undefined,
    ): typeof fn => {
        if (typeof fn !== 'function' || (fn as { [REBASED]?: boolean })[REBASED]) {
            return fn;
        }
        const wrapped = function achRebasedLibraryRequested(
            this: unknown,
            libraryName: string,
        ) {
            const cache = editorNs.libraryCache;
            const entry = cache?.[libraryName] as Record<string, unknown> | undefined;
            if (entry && entry[REBASED] !== true) {
                try {
                    const raw = JSON.stringify(entry);
                    if (raw !== undefined && raw.includes('__H5P_BASE__')) {
                        const rebased = JSON.parse(
                            raw.split('__H5P_BASE__').join(staticBase),
                        ) as Record<string, unknown>;
                        rebased[REBASED] = true;
                        cache![libraryName] = rebased;
                    } else {
                        entry[REBASED] = true;
                    }
                } catch {
                    // Leave a payload that will not round-trip untouched.
                }
            }
            // eslint-disable-next-line prefer-rest-params
            return fn.apply(this, arguments as never);
        };
        (wrapped as { [REBASED]?: boolean })[REBASED] = true;
        return wrapped as typeof fn;
    };

    // EAA-1928: intercept the ASSIGNMENT, do not just replace the current value.
    //
    // Measured on dev (build 518): the previous version installed successfully on the
    // iframe namespace — `__achRebaseInstalled` was true — but the live
    // `libraryRequested` had no wrapper, because the iframe loads its own copy of
    // `h5peditor.js`, which does `ns.libraryRequested = function (...)` and silently
    // discarded it. Ordering cannot be won by racing: whichever side assigns last
    // wins. A defineProperty setter wraps whatever is assigned, whenever it is
    // assigned, so load order stops mattering in both directions.
    let current = wrap(editorNs.libraryRequested);
    const setter = (fn: (libraryName: string, callback?: unknown) => unknown): void => {
        current = wrap(fn);
    };
    // Tag the setter itself so the guard above can recognise OUR accessor on THIS
    // object; a copied boolean cannot impersonate it.
    (setter as { __achOwn?: boolean }).__achOwn = true;
    Object.defineProperty(editorNs, 'libraryRequested', {
        configurable: true,
        enumerable: true,
        get: () => current,
        set: setter,
    });
    editorNs.__achRebaseInstalled = true;
    return true;
}

/**
 * EAA-1029: drop cached semantics when the editor language changes.
 *
 * `h5peditor.js`'s `ns.loadLibrary` keys `ns.libraryCache` by library name ALONE:
 *
 *     switch (ns.libraryCache[libraryName]) {
 *       default: ns.libraryRequested(libraryName, callback); break;  // hit -> no fetch
 *       case undefined: // fetch
 *
 * The cache lives on `window.H5PEditor` and outlives the editor instance, so it
 * survives the deliberate rebuild that `H5PEditor.tsx` performs to apply a language
 * switch. The rebuilt editor therefore fetched the `vi` MODEL — Vietnamese core
 * chrome, Vietnamese validation messages — and then read the still-cached `en`
 * SEMANTICS for every field label. That is the reported bug: "Correct answer",
 * "Blanks used in the text", "Snippets" and the `entity` in "Add blank" stayed
 * English next to Vietnamese chrome, until a full page reload.
 *
 * `generate.mjs` already nulls each library's `language`/`defaultLanguage` because
 * H5P deep-merges those OVER the translated semantics; this closes the same hole on
 * the other side, where a stale entry means the merge never runs at all.
 *
 * Two constraints:
 *
 * - **Delete keys in place; never reassign `libraryCache`.** The form iframe seeds
 *   its namespace with `H5P.jQuery.extend(false, {}, window.parent.H5PEditor)`
 *   (h5peditor.js:7) — a SHALLOW copy, so it shares this object by reference.
 *   Replacing it with a fresh `{}` would leave the iframe holding the old one.
 * - **Leave `ns.libraryLoaded` alone.** It gates CSS/JS injection, which is
 *   language-independent. Clearing it would re-evaluate every library script;
 *   keeping it true makes `libraryRequested` go straight to the callback with the
 *   freshly fetched semantics, which is exactly what is wanted.
 */
function dropSemanticsCacheOnLanguageChange(ns: unknown, language: string | undefined): void {
    if (!ns || !language) {
        return;
    }
    const editorNs = ns as {
        libraryCache?: Record<string, unknown>;
        __achSemanticsLanguage?: string;
    };
    if (editorNs.__achSemanticsLanguage === language) {
        return;
    }
    editorNs.__achSemanticsLanguage = language;
    const cache = editorNs.libraryCache;
    if (!cache) {
        return;
    }
    // On the FIRST render the marker is undefined and the cache is empty, so this
    // loop is a no-op — which is why there is no special case for it. Skipping the
    // clear on a first render would instead trust a cache this component has never
    // seen to be in the right language.
    for (const libraryName of Object.keys(cache)) {
        delete cache[libraryName];
    }
}

/**
 * EAA-1029: one string from H5P's own core editor dictionary, or `fallback`.
 *
 * The dictionary is read DIRECTLY rather than through `H5PEditor.t('core', key)`,
 * because `t` reports a miss by RETURNING prose — `'Missing translation for ' + key`
 * (h5peditor-editor.js:622) — which would then be dispatched to the author as the
 * validation message. There is no return value that means "not translated", so the
 * lookup has to be done here to be able to fall back at all.
 */
function translateCoreString(key: string, fallback: string): string {
    const core = (
        H5PEditor as { language?: { core?: Record<string, unknown> } } | undefined
    )?.language?.core;
    const translated = core?.[key];
    return typeof translated === 'string' && translated.trim() ? translated : fallback;
}

/**
 * EAA-1928: the form iframe builds its namespace asynchronously (h5peditor-editor.js
 * `populateIframe` uses `document.write`), so there is no event to hang this on that
 * fires before the first library load. Poll briefly instead — installation is
 * idempotent and stops as soon as it succeeds or the deadline passes.
 */
function installIframeLibraryCacheRebase(root: ParentNode, staticBase: string | undefined): void {
    if (!staticBase) {
        return;
    }
    const deadline = Date.now() + 15000;
    const tick = (): void => {
        const iframe = root.querySelector('.h5p-editor-iframe') as HTMLIFrameElement | null;
        const win = iframe?.contentWindow as unknown as { H5PEditor?: unknown } | undefined;
        if (win?.H5PEditor && installLibraryCacheRebase(win.H5PEditor, staticBase)) {
            return;
        }
        if (Date.now() < deadline) {
            setTimeout(tick, 50);
        }
    };
    tick();
}

/**
 * EAA-755: mirror the host's light/dark mode into the editor's form iframe.
 *
 * `overrides/ach-editor.css` declares its own copies of the design tokens because
 * `h5peditor-editor.js` writes this iframe from scratch, so the host's `:root`
 * cannot cascade in. It also declares `[data-theme='dark']` / `.dark` blocks — and
 * its own header says why they were never reached: *"the iframe never sees the
 * host's `data-theme` attribute either, so dark mode here can only follow
 * `prefers-color-scheme` … the sheet is already correct if the host ever starts
 * stamping the iframe."* This is the host starting to stamp it.
 *
 * Until now the editor chrome followed the AUTHOR'S OS, not the app: with the app
 * in light mode on a dark-mode OS the form rendered dark inside a light page, and
 * toggling the app's theme changed nothing inside the form.
 *
 * Polled rather than event-driven for the same reason
 * `installIframeLibraryCacheRebase` polls: `populateIframe` builds the document with
 * `document.write`, which discards anything stamped beforehand, and there is no
 * event that fires once it is done. The MutationObserver keeps a later toggle in
 * step without waiting for a reload.
 */
/**
 * EAA-755: the tokens `overrides/ach-editor.css` DECLARES for itself.
 *
 * Unlike the player sheet, which aliases the host's values, the editor sheet has
 * to ship its own copies — `h5peditor-editor.js` writes this iframe from scratch,
 * so nothing cascades in. That works for the stock palette and fails for a TENANT
 * one: the host's brand is applied at runtime to its own `:root`
 * (`theme-runtime.ts`), which this document never sees, so an org with a purple
 * CTA got purple chrome around a stock-teal H5P form.
 *
 * Keep this list in step with the sheet's `:root` block. A token missing here
 * simply keeps the sheet's own default, so the form stays coherent — it just
 * stops following that tenant.
 */
const EDITOR_THEME_TOKENS = [
    '--background',
    '--foreground',
    '--card',
    '--primary',
    '--primary-foreground',
    '--primary-hover',
    '--muted',
    '--muted-foreground',
    '--accent',
    '--accent-foreground',
    '--destructive',
    '--border',
    '--ring',
    '--radius',
] as const;

function mirrorThemeIntoEditorIframe(root: ParentNode): () => void {
    const hostRoot = document.documentElement;
    let stopped = false;

    const apply = (): void => {
        const iframe = root.querySelector('.h5p-editor-iframe') as HTMLIFrameElement | null;
        let doc: Document | null | undefined;
        try {
            doc = iframe?.contentDocument;
        } catch {
            return; // cross-origin: nothing we can reach
        }
        const frameRoot = doc?.documentElement;
        if (!frameRoot) {
            return;
        }
        const mode = hostRoot.getAttribute('data-theme');
        if (mode && frameRoot.getAttribute('data-theme') !== mode) {
            frameRoot.setAttribute('data-theme', mode);
        }
        const wantsDark = mode === 'dark' || hostRoot.classList.contains('dark');
        if (frameRoot.classList.contains('dark') !== wantsDark) {
            frameRoot.classList.toggle('dark', wantsDark);
        }

        // Copy the host's RESOLVED token values in. Appended to <head> after the
        // sheet's own link, so it wins on source order against both the sheet's
        // `:root` block and its `[data-theme='dark']` one — those are the same
        // specificity, and these values are already correct for the active mode
        // AND the active tenant, so they should win in both.
        const head = doc?.head;
        if (!head) {
            return;
        }
        const computed = getComputedStyle(hostRoot);
        const declarations = EDITOR_THEME_TOKENS.map((name) => {
            const value = computed.getPropertyValue(name).trim();
            return value ? `${name}:${value};` : '';
        }).join('');
        if (!declarations) {
            return;
        }
        let style = doc.getElementById('ach-h5p-editor-theme-tokens') as HTMLStyleElement | null;
        if (!style) {
            style = doc.createElement('style');
            style.id = 'ach-h5p-editor-theme-tokens';
            head.appendChild(style);
        }
        const block = `:root{${declarations}}`;
        if (style.textContent !== block) {
            style.textContent = block;
        }
    };

    const deadline = Date.now() + 15000;
    const tick = (): void => {
        if (stopped) {
            return;
        }
        apply();
        if (Date.now() < deadline) {
            setTimeout(tick, 100);
        }
    };
    tick();

    // `style` is watched too: a tenant theme lands as inline custom properties on
    // the host root (`theme-runtime.ts` uses setProperty), which changes neither
    // the attribute nor the class — without it a tenant switch would leave the
    // form on the previous org's palette until the next reload.
    const observer = new MutationObserver(apply);
    observer.observe(hostRoot, {
        attributes: true,
        attributeFilter: ['data-theme', 'class', 'style'],
    });

    return (): void => {
        stopped = true;
        observer.disconnect();
    };
}

export class H5PEditorComponent extends HTMLElement {
    constructor() {
        super();

        H5PEditorComponent.initTemplate();
    }

    public get contentId(): string {
        return this.getAttribute('content-id') ?? undefined;
    }

    public set contentId(contentId: string) {
        if (!contentId) {
            this.removeAttribute('content-id');
        } else {
            this.setAttribute('content-id', contentId);
        }
    }

    /**
     * Called when the component needs to load data about content. The endpoint
     * called in here combines the results of H5PEditor.render(...) and
     * H5PEditor.getContent(...) to avoid too many requests.
     *
     * Note that the library, metadata and params property of the returned
     * object must only be defined if contentId is defined.
     *
     * Should throw an error with a message in the message property if something
     * goes wrong.
     */
    public get loadContentCallback(): (contentId: string) => Promise<
        IEditorModel & {
            library?: string;
            metadata?: IContentMetadata;
            params?: any;
            // urlGenerator: IUrlGenerator;
        }
    > {
        return this.privateLoadContentCallback;
    }

    public set loadContentCallback(
        callback: (contentId: string) => Promise<
            IEditorModel & {
                library?: string;
                metadata?: IContentMetadata;
                params?: any;
                // urlGenerator: IUrlGenerator;
            }
        >
    ) {
        // We only (re-)render the component if the callback was really changed.
        const mustRender = this.privateLoadContentCallback !== callback;
        this.privateLoadContentCallback = callback;
        if (mustRender) {
            this.render(this.contentId);
        }
    }

    /**
     * Indicates changes to which attributes should trigger calls to
     * attributeChangedCallback.
     */
    public static get observedAttributes(): string[] {
        return ['content-id', 'h5p-url'];
    }

    private static template: HTMLTemplateElement;

    /**
     * Called when the component needs to save data about content. The endpoint
     * called here should call H5PEditor.saveOrUpdateContentReturnMetaData(...).
     * Note that it makes sense to use PATCH requests for updates and POST
     * requests for new content, but it's up to you how you implement this. See
     * defaultSaveContentCallback for an example.
     *
     * Should throw an error with a message in the message property if something
     * goes wrong.
     * @param contentId the contentId which needs to be saved; can be undefined
     * for new content, which hasn't been saved before
     * @param requestBody the data needed by the server; usually encoded as JSON
     * string and sent to the server
     * @returns the newly assigned content id and metadata
     */
    public saveContentCallback: (
        contentId: string,
        requestBody: { library: string; params: any }
    ) => Promise<{ contentId: string; metadata: IContentMetadata }>;

    private editorInstance: any;
    /**
     * Stores the H5P instance (H5P native object of the core).
     */

    private privateLoadContentCallback: (contentId: string) => Promise<
        IEditorModel & {
            library?: string;
            metadata?: IContentMetadata;
            params?: any;
        }
    >;

    private resizeObserver: ResizeObserver;

    private root: HTMLElement;

    /** EAA-755: tears down the editor-iframe theme mirror. */
    private stopThemeMirror: (() => void) | null = null;

    private static initTemplate(): void {
        // We create the static template only once
        if (!H5PEditorComponent.template) {
            H5PEditorComponent.template = document.createElement('template');
            H5PEditorComponent.template.innerHTML = `
            <style>
            .h5peditor-semi-fullscreen {
                margin: 0;
                padding: 0;
                position: fixed;
                overflow-y: scroll;
                box-sizing: border-box;
                height: 100%;
                width: 100%;
                left: 0;
                top: 0;
            }
            .h5p-more-libraries,
            .h5p-tutorial-url,
            select[name="h5peditor-library"] {
                display: none;
            }
            </style>
            <div class="h5p-editor-component-root"></div>`;
        }
    }

    /**
     * Called when one of the attributes in observedAttributes changes.
     */
    public async attributeChangedCallback(name: string, oldVal: any, newVal: any): Promise<void> {
        // We don't render if the component's content id changes from 'new'
        // to something else, as this would lead to flickering when saving
        // newly created content.
        if (name === 'content-id' && oldVal !== newVal && oldVal !== 'new') {
            await this.render(newVal);
        }
    }

    /**
     * Called when the component is added to the DOM.
     */
    public async connectedCallback(): Promise<void> {
        this.appendChild(H5PEditorComponent.template.content.cloneNode(true));
        this.root = this.querySelector('.h5p-editor-component-root');

        this.resizeObserver = new ResizeObserver(() => {
            this.resize();
        });
        this.resizeObserver.observe(this);

        // EAA-755: keep the form iframe on the app's theme, not the OS's.
        this.stopThemeMirror = mirrorThemeIntoEditorIframe(this);

        await this.render(this.contentId);
    }

    /**
     * Called when the component is removed from the DOM.
     */
    public disconnectedCallback(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        // EAA-755: stop mirroring the host theme once this editor goes away.
        if (this.stopThemeMirror) {
            this.stopThemeMirror();
            this.stopThemeMirror = null;
        }

        // Unregister our event listener from the global H5P dispatcher.
        if (window.H5P?.externalDispatcher) {
            window.H5P.externalDispatcher.off('editorloaded', this.onEditorLoaded);
        }
    }

    /**
     * Call this method when the iframe containing the editor needs to be
     * resized, e.g. because the some
     */
    public resize = (): void => {
        const h5pEditorIframe = this.querySelector('.h5p-editor-iframe') as HTMLIFrameElement;
        if (h5pEditorIframe) {
            const newHeight = h5pEditorIframe.contentWindow?.document?.body?.scrollHeight;
            if (newHeight !== undefined) {
                h5pEditorIframe.style.height = `${h5pEditorIframe.contentWindow.document.body.scrollHeight.toString()}px`;
            }
            try {
                h5pEditorIframe.contentWindow?.document?.head?.append(`
                    <style>
                        .h5p-more-libraries,
                        .h5p-tutorial-url,
                        .h5p-example-url,
                        select[name="h5peditor-library"] {
                            display: none;
                        }
                    </style>
                    `);
            } catch (e) {
                console.log(e);
            }
        }
    };

    /**
     * Get the current content data from the H5P editor without saving.
     * This validates the content and returns the library, params, and metadata.
     * The component emits 'validation-error' events if validation fails.
     * @throws an error if validation fails
     * @returns the content data { library, params, metadata } or null if not ready
     */
    public getContent = async (): Promise<{
        library: string;
        params: { params: unknown; metadata: IContentMetadata };
    } | null> => {
        if (this.editorInstance === undefined) {
            this.dispatchAndThrowError('validation-error', 'editorInstance of h5p editor not defined.');
            return null;
        }

        // Get parameters (and also validates them as a side effect)
        const editorParams = this.editorInstance.getParams();
        if (!editorParams.params) {
            // EAA-1029: this banner is shown to the AUTHOR on every failed save, so it
            // cannot stay an English literal. `errorParamsBroken` is the H5P core key
            // for exactly this condition ("Parameters are broken." / "Tham số bị
            // hỏng."), and the core dictionary already follows the editor language —
            // the model's script reference is swapped to `language/vi.js` at generate
            // time. Going through `t()` therefore needs no ACH translation key and
            // cannot drift from the surrounding chrome. The literal stays as the
            // fallback for the case where the core dictionary has not loaded.
            this.dispatchAndThrowError(
                'validation-error',
                translateCoreString(
                    'errorParamsBroken',
                    'The parameters entered by the user are invalid.'
                )
            );
            return null;
        }

        // Validate mandatory main title
        if (!this.editorInstance.isMainTitleSet()) {
            // this.dispatchAndThrowError(
            //     'validation-error',
            //     "The main title of the content hasn't been set."
            // );
            return null;
        }

        let content: { library: string; params: string };
        try {
            content = await new Promise<{ library: string; params: string }>((res, rej) => {
                this.editorInstance.getContent(
                    (c) => {
                        res(c);
                    },
                    (err) => {
                        rej(err);
                    }
                );
            });
        } catch (error) {
            this.dispatchAndThrowError('validation-error', error.toString());
            return null;
        }

        return {
            library: content.library,
            params: JSON.parse(content.params)
        };
    };

    /**
     * EAA-1029: read the author's current work WITHOUT validating it.
     *
     * `getContent()` is the save path and refuses incomplete work: it returns null
     * when `getParams()` reports invalid params, and — silently, with no event —
     * when `isMainTitleSet()` is false. That is right for saving and wrong for
     * preserving: a half-filled form is exactly what must survive an ACH language
     * switch, which rebuilds the editor because H5P binds its labels at
     * construction.
     *
     * `getParams(true)` is H5P's own unvalidated read; the `true` also keeps the
     * form from being marked submitted, so asking does not paint the red
     * "required" styling onto fields the author has not reached yet.
     *
     * NEVER use this to save. It can return params H5P considers invalid.
     */
    public getContentSnapshot = (): {
        library: string;
        params: { params: unknown; metadata: IContentMetadata };
    } | null => {
        if (this.editorInstance === undefined) {
            return null;
        }
        const snapshot = this.editorInstance.getParams(true);
        if (!snapshot) {
            return null;
        }
        return {
            library: this.editorInstance.getLibrary(),
            params: { params: snapshot.params, metadata: snapshot.metadata }
        };
    };

    /**
     * Call save() to get data from the H5P editor and send it to the server.
     * You can use the saveContentCallback hook to customize server requests.
     * The component emits 'saved', 'save-error' and 'validation-error' events,
     * depending on success of the function. You can subscribe to those or use
     * the promise's return value and catch the errors in a try-catch block.
     * @throws an error if something went wrong
     * @returns the contentId and metadata of the saved content
     */
    public save = async (): Promise<{
        contentId: string;
        metadata: IContentMetadata;
    }> => {
        if (!this.saveContentCallback) {
            this.dispatchAndThrowError(
                'save-error',
                'saveContentCallback of H5P Editor Web Component not defined.'
            );
        }

        const content = await this.getContent();
        if (!content) {
            return;
        }

        let saveResponseObject: {
            contentId: string;
            metadata: IContentMetadata;
        };

        const requestBody = {
            library: content.library,
            params: content.params
        };
        try {
            saveResponseObject = await this.saveContentCallback(
                this.contentId === 'new' ? undefined : this.contentId,
                requestBody
            );
        } catch (error) {
            this.dispatchAndThrowError('save-error', error.message);
        }

        if (this.contentId !== saveResponseObject.contentId) {
            this.setAttribute('content-id', saveResponseObject.contentId);
        }

        this.dispatchEvent(
            new CustomEvent('saved', {
                detail: {
                    contentId: saveResponseObject.contentId,
                    metadata: saveResponseObject.metadata
                }
            })
        );
        return saveResponseObject;
    };

    /**
     * Dispatches an event of the specified name and throws an error whose error
     * message starts with the eventName.
     * @param eventName
     * @param message
     */
    private dispatchAndThrowError(eventName: string, message: string): void {
        this.dispatchEvent(
            new CustomEvent(eventName, {
                detail: {
                    message
                }
            })
        );
        throw new Error(`${eventName}: ${message}`);
    }

    /**
     * Called by the global H5P event dispatcher when any editor was loaded.
     */
    private onEditorLoaded = (event: { data: string }): void => {
        // We must manually check if our editor instance is initialized, as the
        // event is only sent globally.
        //
        // EAA-1029: optional chaining, because the check itself used to throw. The
        // event is GLOBAL, so it also fires for editors belonging to other
        // component instances and for ones whose `editorInstance` is not assigned
        // yet — reading `.selector` through an unset instance threw
        // "Cannot read properties of undefined (reading 'form')". This handler runs
        // inside H5P's own dispatcher, so that throw broke the whole handler chain:
        // every later listener was skipped and the editor never finished loading.
        if (this.editorInstance?.selector?.form) {
            this.dispatchEvent(
                new CustomEvent('editorloaded', {
                    detail: { contentId: this.contentId, ubername: event.data }
                })
            );
            // After our editor has been initialized, it will never fire the
            // global event again, so we can unsubscribe from it.
            if (window.H5P?.externalDispatcher) {
                window.H5P.externalDispatcher.off('editorloaded', this.onEditorLoaded);
            }
        }
    };

    /**
     * Displays the editor inside the component by creating a new DOM tree.
     * @param contentId the content id to display or undefined if a new piece
     * of content was created
     */
    private async render(contentId?: string): Promise<void> {
        if (!this.loadContentCallback) {
            return;
        }

        if (!contentId) {
            return;
        }

        let editorModel: IEditorModel & {
            library?: string;
            metadata?: IContentMetadata;
            params?: any;
        };
        try {
            editorModel = await this.loadContentCallback(
                contentId === 'new' ? undefined : contentId
            );
        } catch (error) {
            this.root.innerHTML = `<p>Error loading H5P content from server: ${error.message}`;
            return;
        }

        // Reset DOM tree inside component.
        this.root.innerHTML = '';

        // We have to prevent H5P from initializing when the h5p.js file is
        // loaded.
        if (!window.H5P) {
            window.H5P = {} as any;
        }
        window.H5P.preventInit = true;

        // We merge the H5P integration we received from the server with the one
        // that already exists in the window globally to allow for several H5P
        // content objects on a single page.
        mergeH5PIntegration(editorModel.integration, contentId === 'new' ? undefined : contentId);

        const urlGenerator = editorModel.urlGenerator as UrlGenerator;
        // As the editor adds an iframe anyway and styles don't really matter, we
        // only add the global editor scripts to the whole page, but not the styles
        // (to avoid side effects).

        await addScripts(
            editorModel.scripts,
            document.getElementsByTagName('head')[0],
        );

        try {
            document.getElementsByTagName('head')[0].append(` <style>
                    .h5p-more-libraries,
                    .h5p-tutorial-url,
                    .h5p-example-url,
                    select[name="h5peditor-library"] {
                        display: none;
                    }
                </style>`);
        } catch (e) {
            console.log(e);
        }

        // Create the necessary DOM tree.
        const h5pCreateDiv = document.createElement('div');
        h5pCreateDiv.className = 'h5p-create';
        h5pCreateDiv.hidden = true;
        this.root.appendChild(h5pCreateDiv);
        const h5pEditorDiv = document.createElement('div');
        h5pEditorDiv.className = 'h5p-editor';
        h5pCreateDiv.appendChild(h5pEditorDiv);

        // EAA-1928: rewrite `__H5P_BASE__` inside every library payload H5P fetches
        // for itself.
        //
        // The `getAjaxUrl` override below points `action=libraries` at the static
        // bundle, but H5P then loads that JSON with its OWN jQuery and consumes
        // `libraryData.css` / `.javascript` verbatim in `libraryRequested`. Those
        // arrays are stored with the portable `__H5P_BASE__` token (117 of them in
        // editors/vi/H5P.AdvancedBlanks-1.2.json), and nothing on our side ever sees
        // that response — so the token reached the DOM unresolved and the browser
        // requested `/question-bank/__H5P_BASE__/libraries/...`, 404ing every editor
        // script and stylesheet. That is what kept the deployed editor falling back to
        // the H5P server even once the bundle was shipped.
        //
        // `libraryRequested` is the right seam for the same reason `getAjaxUrl` is:
        // the form's iframe seeds its namespace by shallow-copying
        // `window.parent.H5PEditor`, so a wrapper installed here is inherited, while
        // an XHR interceptor on the host would never see the iframe's requests.
        // h5peditor.js is already loaded by `addScripts` above, so the original exists.
        const staticBase = (
            editorModel.integration.editor as { staticBase?: string }
        ).staticBase;
        // EAA-1928: install on the IFRAME's namespace, not just the parent's.
        //
        // Measured on dev: `iframe.contentWindow.H5PEditor !== window.H5PEditor`, and
        // the iframe's own `libraryCache` is what holds the tokenised payload. The
        // comment on `getAjaxUrl` below claims the form iframe "seeds its namespace by
        // shallow-copying window.parent.H5PEditor" — that is NOT true for
        // `libraryRequested`, and three earlier attempts to patch the parent had no
        // effect for exactly that reason. Whatever makes `getAjaxUrl` reachable does
        // not extend here, so both namespaces get patched.
        installLibraryCacheRebase(H5PEditor, staticBase);

        // EAA-1029: must happen BEFORE `new ns.Editor(...)` below, and on the PARENT
        // namespace — the form iframe shallow-copies this namespace when it is built,
        // so clearing here is what the iframe then inherits.
        dropSemanticsCacheOnLanguageChange(
            H5PEditor,
            editorModel.integration.editor.language,
        );

        // Set up the H5P core editor.
        H5PEditor.getAjaxUrl = (action: string, parameters: { [x: string]: any }): string => {
            // EAA-1029: serve per-library semantics from the static bundle instead of
            // the H5P server, when the editor model says one is available
            // (`staticLibrariesPath`, written by apps/h5p-core's generate.mjs and
            // already rebased to the real base by fetchStaticH5PEditorModel).
            //
            // It has to happen HERE, not in an ajax interceptor on the host: the editor
            // form runs in its own iframe, which seeds its namespace by shallow-copying
            // `window.parent.H5PEditor` — so it inherits THIS function, but its jQuery
            // and XHR are the iframe's own and nothing registered on the host can see
            // them. `getAjaxUrl` is the one hook that crosses that boundary.
            const staticLibrariesPath = (
                editorModel.integration.editor as { staticLibrariesPath?: string }
            ).staticLibrariesPath;
            if (action === 'libraries' && staticLibrariesPath && parameters?.machineName) {
                const { machineName, majorVersion, minorVersion } = parameters;
                if (majorVersion !== undefined && minorVersion !== undefined) {
                    const ubername = `${machineName}-${majorVersion}.${minorVersion}`;
                    return `${staticLibrariesPath.replace(/\/+$/, '')}/${encodeURIComponent(ubername)}.json`;
                }
            }

            let url = editorModel.integration.editor.ajaxPath + action;

            if (parameters !== undefined) {
                for (const property in parameters) {
                    if (Object.prototype.hasOwnProperty.call(parameters, property)) {
                        url = `${url}&${property}=${parameters[property]}`;
                    }
                }
            }

            // Append query string from current page, replacing ? with & (if present)
            // This allows passing through query parameters like language, etc.
            // But we should filter out the 'library' parameter as it's only for the edit page
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.delete('library'); // Remove library parameter from AJAX calls

            if (searchParams.toString()) {
                url += `&${searchParams.toString()}`;
            }

            return url;
        };

        window.H5P.preventInit = false;
        // Only initialize H5P once to avoid resetting values.
        if (!window.h5pIsInitialized) {
            window.H5P.init(this.root);
            window.h5pIsInitialized = true;
        }

        // Register our global editorloaded event handler.
        if (window.H5P.externalDispatcher) {
            window.H5P.externalDispatcher.on('editorloaded', this.onEditorLoaded, this);
        }

        // Configure the H5P core editor.
        H5PEditor.$ = window.H5P.jQuery;
        H5PEditor.basePath = editorModel.integration.editor.libraryUrl;
        H5PEditor.fileIcon = editorModel.integration.editor.fileIcon;
        H5PEditor.ajaxPath = editorModel.integration.editor.ajaxPath;
        H5PEditor.filesPath = editorModel.integration.editor.filesPath;
        H5PEditor.apiVersion = editorModel.integration.editor.apiVersion;
        H5PEditor.contentLanguage = editorModel.integration.editor.language;
        H5PEditor.copyrightSemantics = editorModel.integration.editor.copyrightSemantics;
        H5PEditor.metadataSemantics = editorModel.integration.editor.metadataSemantics;
        H5PEditor.assets = editorModel.integration.editor.assets;
        H5PEditor.baseUrl = '';
        H5PEditor.contentId = contentId === 'new' ? undefined : contentId;
        H5PEditor.enableContentHub = editorModel.integration.editor.enableContentHub || false;

        if (contentId === 'new') {
            // Create editor with library if provided, otherwise empty
            if (editorModel.library) {
                this.editorInstance = new ns.Editor(
                    editorModel.library,
                    undefined,
                    h5pEditorDiv
                );
            } else {
                this.editorInstance = new ns.Editor(undefined, undefined, h5pEditorDiv);
            }
        } else {
            // Load the editor with populated parameters for existing content
            this.editorInstance = new ns.Editor(
                editorModel.library,
                JSON.stringify({
                    metadata: editorModel.metadata,
                    params: editorModel.params
                }),
                h5pEditorDiv
            );
        }

        // EAA-1928: the iframe only exists once ns.Editor has built it, so start the
        // poller here — before any library load can complete.
        installIframeLibraryCacheRebase(this, staticBase);

        h5pCreateDiv.hidden = false;
    }
}
